import ArchiveIcon from '@mui/icons-material/Archive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  alpha,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Tender, TenderStatus } from '../../../types/tender';
import { calculateTenderStep } from '../../../utils/tenderProgress';
import TenderActionStepper from '../TenderActionStepper';
import { displayDaysUntilDeadline, getDeadlineDate } from './helpers';

interface TenderCardProps {
  tender: Tender;
  account: { uid?: string; clientId?: string } | null;
  partnersMap: Record<string, string>;
  usersMap?: Record<string, { displayName: string; teamId?: string }> | undefined;
  teamsMap?: Record<string, string> | undefined;
  onCalendarClick: (e: React.MouseEvent<HTMLButtonElement>, tender: Tender) => void;
  onStepperDownload: (
    tender: Tender,
    type: 'alignment' | 'response' | 'review' | 'compile'
  ) => void;
  isFileProcessing: (
    tender: Tender,
    type: 'analysis' | 'response' | 'review' | 'compile'
  ) => boolean;
  isLoadingFile: (tenderId: string, type: string) => boolean;
  onOpenFeedback: (tender: Tender) => void;
  onArchiveClick: (tender: Tender) => void;
  primaryButtonStyle: import('@mui/system').SxProps<import('@mui/material/styles').Theme>;
}

const TenderCard: React.FC<TenderCardProps> = ({
  tender,
  account,
  partnersMap,
  usersMap,
  teamsMap,
  onCalendarClick,
  onStepperDownload,
  isFileProcessing,
  isLoadingFile,
  onOpenFeedback,
  onArchiveClick,
  primaryButtonStyle,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const processingText = '[extracting tender details]';
  const isProcessing =
    (tender.status as TenderStatus | 'Processing Initial Docs' | 'Preparing documents') ===
      'Processing Initial Docs' ||
    (tender.status as TenderStatus | 'Processing Initial Docs' | 'Preparing documents') ===
      'Preparing documents' ||
    !tender.tenderIssuer;

  const deadlineDateObj = getDeadlineDate(tender);
  let closingDateDisplay = 'N/A';
  let daysUntilDeadlineNumeric: number | null = null;

  if (deadlineDateObj && !Number.isNaN(deadlineDateObj.getTime())) {
    closingDateDisplay = deadlineDateObj.toLocaleDateString();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const deadlineDay = new Date(
      deadlineDateObj.getFullYear(),
      deadlineDateObj.getMonth(),
      deadlineDateObj.getDate()
    );
    const diffTime = deadlineDay.getTime() - today.getTime();
    daysUntilDeadlineNumeric = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } else {
    if (typeof tender.daysUntilDeadline === 'number') {
      daysUntilDeadlineNumeric = tender.daysUntilDeadline;
    }
  }

  const isClosed = typeof daysUntilDeadlineNumeric === 'number' && daysUntilDeadlineNumeric < 0;
  const isUrgent =
    !isClosed && typeof daysUntilDeadlineNumeric === 'number' && daysUntilDeadlineNumeric < 5;
  const isApproaching =
    !isClosed && typeof daysUntilDeadlineNumeric === 'number' && daysUntilDeadlineNumeric <= 7;

  let borderColor = theme.palette.greyContrast;
  if (isClosed) {
    borderColor = theme.palette.action.disabled;
  } else if (isUrgent) {
    borderColor = theme.palette.bidlogiq?.rust || theme.palette.error.main;
  } else if (isApproaching) {
    borderColor = theme.palette.bidlogiq?.brick || theme.palette.warning.main;
  }

  const stepInfo = calculateTenderStep(tender);

  return (
    <Card
      data-testid="tender-card"
      data-tender-id={tender.id}
      sx={{
        position: 'relative',
        mb: 2.5,
        boxShadow: isUrgent
          ? `0 4px 8px ${theme.palette.bidlogiq?.rust || theme.palette.error.main}`
          : `0 2px 4px ${theme.palette.greyContrast}`,
        borderLeft: `6px solid ${tender.isGrant ? theme.palette.success.main : borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.greyLight,
        color: isClosed ? theme.palette.greyContrast : theme.palette.text.primary,
        borderRadius: 3,
      }}
    >
      {isClosed ? (
        <CardContent sx={{ p: 2, pb: '8px !important' }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 'bold',
              color: 'inherit',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {tender.title || 'No Title'}
            {tender.isGrant && (
              <Chip label="Grant" color="success" size="small" sx={{ fontWeight: 'bold' }} />
            )}
            <Typography component="span" sx={{ fontWeight: 'normal', fontStyle: 'italic', ml: 1 }}>
              (closed, {Math.abs(daysUntilDeadlineNumeric ?? 0)}{' '}
              {Math.abs(daysUntilDeadlineNumeric ?? 0) === 1 ? 'day' : 'days'} ago)
            </Typography>
          </Typography>
        </CardContent>
      ) : (
        <CardContent
          sx={{
            position: 'relative',
            pb: '8px !important',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            pt: '16px !important',
          }}
        >
          <Grid container spacing={2} alignItems="flex-start" justifyContent="space-between">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  wordWrap: 'break-word',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {tender.title || (isProcessing ? 'Processing Upload...' : 'No Title')}
                {tender.isGrant && (
                  <Chip label="Grant" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                )}
                {tender.isComplex && (
                  <Tooltip
                    title="This Request has a massive amount of attachments. The AI may not have been able to read every single file due to token limits. Please review the 'Capability Analysis' report."
                    slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                    arrow
                  >
                    <Chip
                      label="Complex"
                      color="warning"
                      size="medium"
                      sx={{ fontWeight: 'bold', cursor: 'help' }}
                    />
                  </Tooltip>
                )}
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', md: 'flex-end' },
                justifyContent: 'flex-start',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color:
                    stepInfo.color === 'primary'
                      ? theme.palette.primary.main
                      : stepInfo.color === 'secondary'
                        ? theme.palette.secondary.dark
                        : stepInfo.color === 'error'
                          ? theme.palette.error.main
                          : stepInfo.color === 'warning'
                            ? theme.palette.warning.main
                            : stepInfo.color === 'info'
                              ? theme.palette.info.main
                              : stepInfo.color === 'success'
                                ? theme.palette.success.main
                                : theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Next Action:{' '}
                {stepInfo.label === 'RETRIEVE FILES' && tender.tenderSourceURL ? (
                  <a
                    href={tender.tenderSourceURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    {stepInfo.label}
                  </a>
                ) : (
                  stepInfo.label
                )}
              </Typography>
              {tender.pursuitStatus === 'watching' && (
                <Chip
                  label="Go/No-Go"
                  size="small"
                  sx={{
                    mt: 1,
                    backgroundColor: theme.palette.bidlogiq?.rust || theme.palette.error.main,
                    color: '#ffffff',
                    fontWeight: 'bold',
                  }}
                />
              )}
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 1, md: 0 }, width: '100%', alignItems: 'flex-end' }}
          >
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ display: 'flex', mb: 0.4 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1rem',
                    color: theme.palette.text.primary,
                    fontWeight: 'bold',
                    mr: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Issued by:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
                >
                  {isProcessing ? processingText : tender.tenderIssuer || 'IssuerMissing'}
                </Typography>
              </Box>

              {(tender.tenderId || tender.opportunityID || tender.tenderSourceURL) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.4 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1rem',
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      mr: 0.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Tender ID:
                  </Typography>
                  {tender.tenderSourceURL ? (
                    <a
                      href={tender.tenderSourceURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '1.05rem',
                        color: theme.palette.text.primary,
                        fontWeight: 800,
                        textDecoration: 'underline',
                      }}
                    >
                      {tender.tenderId || tender.opportunityID || tender.id || 'View Source'}
                    </a>
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{ fontSize: '1rem', color: theme.palette.text.primary, fontWeight: 600 }}
                    >
                      {tender.tenderId || tender.opportunityID || tender.id || 'N/A'}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.4 }}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1rem', color: theme.palette.text.primary, mr: 1 }}
                >
                  <strong>Closing date:</strong>{' '}
                  {isProcessing
                    ? processingText
                    : closingDateDisplay === 'N/A'
                      ? 'Unknown, visit portal'
                      : `${closingDateDisplay} (${displayDaysUntilDeadline(daysUntilDeadlineNumeric)})`}
                </Typography>
                {!isProcessing && (
                  <Tooltip title="Add to calendar">
                    <IconButton
                      size="small"
                      onClick={(e) => onCalendarClick(e, tender)}
                      sx={{
                        padding: 0,
                        color: theme.palette.text.secondary,
                        '&:hover': { color: theme.palette.primary.main },
                      }}
                    >
                      <CalendarTodayIcon
                        fontSize="small"
                        sx={{
                          fontSize: '1rem',
                          color: theme.palette.bidlogiq?.brick || theme.palette.primary.main,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography
                variant="body1"
                sx={{ mb: 0.4, fontSize: '1rem', color: theme.palette.text.primary }}
              >
                <strong>Category:</strong> {tender.category || 'No category provided'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 0,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                }}
              >
                Alignment Score:{' '}
                {isProcessing
                  ? processingText
                  : tender.score !== undefined && tender.score !== null
                    ? `${tender.score}/100`
                    : 'N/A'}
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12, md: 7 }}
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                pb: 0,
                pt: 2,
              }}
            >
              {tender.pursuitStatus !== 'watching' && (
                <TenderActionStepper
                  tender={tender}
                  onDownload={(type) => onStepperDownload(tender, type)}
                  isProcessing={(type) => isFileProcessing(tender, type)}
                  isLoading={(type) => !!isLoadingFile(tender.id || '', type)}
                />
              )}
            </Grid>
          </Grid>
        </CardContent>
      )}
      <CardActions
        sx={{
          justifyContent: 'space-between',
          padding: '8px 16px',
          borderTop: `1px solid ${theme.palette.grey?.[300] || '#ccc'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              navigate(`/tender/${tender.id}`);
            }}
            sx={{
              ...primaryButtonStyle,
              mb: 0.5,
              ...(isClosed && {
                backgroundColor: theme.palette.greyContrast,
                color: `${theme.palette.greyLight} !important`,
                '&:hover': { backgroundColor: theme.palette.greyContrast },
              }),
            }}
          >
            Open Tender Toolkit
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => onOpenFeedback(tender)}
            sx={{ ...primaryButtonStyle, fontSize: '0.8rem', padding: '4px 8px' }}
          >
            Feedback
          </Button>

          {tender.noFiles && (
            <Chip
              label="No Docs, Visit Portal"
              component="a"
              href={tender.tenderSourceURL || ''}
              target="_blank"
              clickable
              variant="outlined"
              sx={{
                backgroundColor: theme.palette.greyContrast,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.bidlogiq?.orange || theme.palette.primary.main,
                borderWidth: 2,
                fontWeight: 'bold',
                fontSize: '0.85rem',
                height: 28,
                '&&:hover': {
                  backgroundColor: theme.palette.bidlogiq?.brick || theme.palette.primary.dark,
                  color: '#fff',
                  borderColor: theme.palette.bidlogiq?.brick || theme.palette.primary.dark,
                },
              }}
            />
          )}

          {account?.uid && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={
                  account.clientId !== tender.clientId
                    ? `PARTNER: ${tender.clientName || partnersMap[tender.clientId || ''] || 'External Company'}`
                    : account.uid === tender.ownerId
                      ? 'OWNER: Me'
                      : `OWNER: ${tender.ownerId && usersMap?.[tender.ownerId]?.displayName ? usersMap[tender.ownerId]?.displayName : 'Unknown'}`
                }
                variant={account.clientId !== tender.clientId ? 'filled' : 'outlined'}
                sx={{
                  backgroundColor: account.clientId !== tender.clientId ? '#C19A6B' : 'transparent',
                  borderColor:
                    account.clientId !== tender.clientId
                      ? '#C19A6B'
                      : account.uid === tender.ownerId
                        ? theme.palette.success.main
                        : theme.palette.info.main,
                  color:
                    account.clientId !== tender.clientId
                      ? '#000000'
                      : account.uid === tender.ownerId
                        ? theme.palette.success.main
                        : theme.palette.info.main,
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  height: 28,
                }}
              />

              {tender.ownerId &&
                usersMap?.[tender.ownerId]?.teamId &&
                teamsMap?.[usersMap[tender.ownerId]?.teamId || ''] && (
                  <Chip
                    label={teamsMap[usersMap[tender.ownerId]?.teamId || '']}
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.info.main,
                      color: theme.palette.info.main,
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      height: 28,
                    }}
                  />
                )}

              {account.clientId === tender.clientId &&
                (() => {
                  const linkedPartnersCount =
                    tender.alignmentAnalysis?.recommendedPartners?.filter(
                      (p) => p.userId && tender.sharedWith?.includes(p.userId)
                    ).length || 0;
                  if (linkedPartnersCount === 0) {
                    return null;
                  }
                  return (
                    <Chip
                      label={`${linkedPartnersCount} Partner${linkedPartnersCount !== 1 ? 's' : ''} Linked`}
                      variant="outlined"
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        height: 28,
                        backgroundColor: `${theme.palette.primary.main}1A`,
                      }}
                    />
                  );
                })()}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {account?.uid === tender.ownerId &&
            (isLgUp ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<ArchiveIcon />}
                onClick={() => onArchiveClick(tender)}
                sx={{ ...primaryButtonStyle, fontSize: '0.8rem', padding: '4px 8px' }}
              >
                Archive
              </Button>
            ) : (
              <IconButton
                onClick={() => onArchiveClick(tender)}
                title="Archive Tender"
                sx={{
                  color: isClosed ? theme.palette.greyContrast : theme.palette.secondary.dark,
                  width: 36,
                  height: 36,
                  '&:hover': { backgroundColor: alpha(theme.palette.secondary.dark, 0.1) },
                }}
              >
                <ArchiveIcon fontSize="large" />
              </IconButton>
            ))}
        </Box>
      </CardActions>
    </Card>
  );
};

export default TenderCard;
